import { apiFetch } from './client'
import type {
  AdminDepartment,
  AdminDocumentsPage,
  AdminDocumentsQuery,
  AdminFeedback,
  AdminJob,
  AdminOverview,
  AdminSource,
  ConversationDetail,
  ConversationPatch,
  ConversationSummary,
  AdminUser,
  DocumentDetail,
  FeedbackRequest,
  LibraryPage,
  MetaUser,
  NewUserRequest,
  Upload,
  UserPatch,
  UserWithPassword,
  GapStatus,
  KnowledgeGap,
  Meta,
  SearchDebug,
  SearchDebugRequest,
  SyncResponse,
  WebsiteSourceRequest,
} from './types'

const enc = encodeURIComponent

export const api = {
  /** Callable without a token: it tells us whether sign-in is needed at all. */
  getMeta: (options: { auth?: boolean; signal?: AbortSignal } = {}) =>
    apiFetch<Meta>('/api/meta', { auth: options.auth ?? true, signal: options.signal }),

  listConversations: (signal?: AbortSignal) => apiFetch<ConversationSummary[]>('/api/conversations', { signal }),

  getConversation: (id: string, signal?: AbortSignal) =>
    apiFetch<ConversationDetail>(`/api/conversations/${enc(id)}`, { signal }),

  updateConversation: (id: string, patch: ConversationPatch) =>
    apiFetch<ConversationSummary>(`/api/conversations/${enc(id)}`, { method: 'PATCH', body: patch }),

  deleteConversation: (id: string) => apiFetch<void>(`/api/conversations/${enc(id)}`, { method: 'DELETE' }),

  sendFeedback: (messageId: string, body: FeedbackRequest) =>
    apiFetch<void>(`/api/messages/${enc(messageId)}/feedback`, { method: 'POST', body }),

  auth: {
    login: (email: string, password: string) =>
      apiFetch<{ user: MetaUser }>('/api/auth/login', { method: 'POST', body: { email, password }, auth: false }),
    logout: () => apiFetch<void>('/api/auth/logout', { method: 'POST' }),
    changePassword: (current_password: string, new_password: string) =>
      apiFetch<{ user: MetaUser }>('/api/auth/change-password', {
        method: 'POST',
        body: { current_password, new_password },
      }),
  },

  documents: {
    library: (
      query: { q?: string; department?: string | null; limit?: number; offset?: number },
      signal?: AbortSignal,
    ) =>
      apiFetch<LibraryPage>('/api/documents', {
        query: {
          q: query.q ?? '',
          department: query.department ?? undefined,
          limit: query.limit ?? 60,
          offset: query.offset ?? 0,
        },
        signal,
      }),
    get: (id: number, signal?: AbortSignal) => apiFetch<DocumentDetail>(`/api/documents/${id}`, { signal }),
  },

  uploads: {
    list: (scope: 'mine' | 'all', signal?: AbortSignal) =>
      apiFetch<Upload[]>('/api/uploads', { query: { scope }, signal }),
    remove: (id: string) => apiFetch<void>(`/api/uploads/${enc(id)}`, { method: 'DELETE' }),
  },

  admin: {
    overview: (signal?: AbortSignal) => apiFetch<AdminOverview>('/api/admin/overview', { signal }),

    sources: (signal?: AbortSignal) => apiFetch<AdminSource[]>('/api/admin/sources', { signal }),

    syncSource: (id: number) => apiFetch<SyncResponse>(`/api/admin/sources/${id}/sync`, { method: 'POST' }),
    addWebsite: (body: WebsiteSourceRequest) =>
      apiFetch<AdminSource>('/api/admin/sources', { method: 'POST', body }),
    updateWebsite: (id: number, patch: Partial<WebsiteSourceRequest>) =>
      apiFetch<AdminSource>(`/api/admin/sources/${id}`, { method: 'PATCH', body: patch }),
    removeSource: (id: number) => apiFetch<void>(`/api/admin/sources/${id}`, { method: 'DELETE' }),

    jobs: (limit = 20, signal?: AbortSignal) => apiFetch<AdminJob[]>('/api/admin/jobs', { query: { limit }, signal }),

    documents: (query: AdminDocumentsQuery, signal?: AbortSignal) =>
      apiFetch<AdminDocumentsPage>('/api/admin/documents', {
        query: {
          q: query.q ?? '',
          source_id: query.source_id ?? undefined,
          status: query.status ?? undefined,
          limit: query.limit ?? 50,
          offset: query.offset ?? 0,
        },
        signal,
      }),

    gaps: (status: GapStatus, signal?: AbortSignal) =>
      apiFetch<KnowledgeGap[]>('/api/admin/gaps', { query: { status }, signal }),

    resolveGap: (id: number | string) =>
      apiFetch<void>(`/api/admin/gaps/${enc(String(id))}/resolve`, { method: 'POST' }),

    feedback: (limit = 50, signal?: AbortSignal) =>
      apiFetch<AdminFeedback[]>('/api/admin/feedback', { query: { rating: -1, limit }, signal }),

    searchDebug: (body: SearchDebugRequest) =>
      apiFetch<SearchDebug>('/api/admin/search-debug', { method: 'POST', body }),

    users: (signal?: AbortSignal) => apiFetch<AdminUser[]>('/api/admin/users', { signal }),
    addUser: (body: NewUserRequest) => apiFetch<UserWithPassword>('/api/admin/users', { method: 'POST', body }),
    updateUser: (id: string, patch: UserPatch) =>
      apiFetch<AdminUser>(`/api/admin/users/${enc(id)}`, { method: 'PATCH', body: patch }),
    resetPassword: (id: string) =>
      apiFetch<UserWithPassword>(`/api/admin/users/${enc(id)}/reset-password`, { method: 'POST' }),
    removeUser: (id: string) => apiFetch<void>(`/api/admin/users/${enc(id)}`, { method: 'DELETE' }),

    departments: (signal?: AbortSignal) => apiFetch<AdminDepartment[]>('/api/admin/departments', { signal }),
    addDepartment: (name: string) =>
      apiFetch<AdminDepartment>('/api/admin/departments', { method: 'POST', body: { name } }),
    renameDepartment: (id: number, name: string) =>
      apiFetch<AdminDepartment>(`/api/admin/departments/${id}`, { method: 'PATCH', body: { name } }),
    removeDepartment: (id: number) => apiFetch<void>(`/api/admin/departments/${id}`, { method: 'DELETE' }),
  },
}
